import React, { useCallback, useEffect, useState } from 'react';
import { MutationStatus } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  ContentTemplate,
  ErrorInfo,
  Grid,
  KeyValue,
  SectionHeader,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UploadedFile } from 'web-library/models/UploadedFile';
import DocumentBadge from '../../../components/DocumentBadge';
import { formatBytes } from '../../../utils/FileUtils';
import RouteKey from '../../../routes/key';
import UploadDocuments from '../../accessArrangement/SelectCondition/components/UploadDocuments';
import { ApplicationType, UploadDocumentType } from '../../../api/candidate/config/model';
import useSetUploadButtonStatus from '../../../hooks/useSetUploadButtonStatus';
import usePostPresignLinks from '../../../modules/withdrawal/logic/usePostPresignLinks';
import useUploadDocument from '../../../modules/withdrawal/logic/useUploadDocument';
import WithdrawalReasonButtons from '../../../modules/withdrawal/WithdrawalReasonButtons';
import { GetWithdrawalDraftResponse } from '../../../api/candidate/exam/model';
import { useGetNonNationalSubmittedExamWithdrawal } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalSubmittedExamWithdrawal';
import { WithdrawalReasonTitleHeader } from './WithdrawalReason';
import { useDisplayFilesExceedError } from '../../../modules/withdrawal/UploadAdditionalDocument';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      left: '-15%',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-14%',
    },
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-13%',
    },
    [theme.breakpoints.down('sm')]: {
      left: '-6%',
    },
  },
  documentsContainer: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(4),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  buttonContainer: {
    display: 'flex',
    marginBottom: theme.spacing(5),
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    color: theme.color.text.textHeader,
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
  errString: {
    color: theme.color.state.stateError,
  },
}));

const { withdrawal } = RouteKey.child;
const { examRegistrations, nonNationalReview, nonNationalSummary } = RouteKey.withdrawal.child;

const UploadAdditionalDocument = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const history = useHistory();

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [filesSubmitted, setFilesSubmitted] = useState<UploadedFile[]>();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isFileRejected, setIsFileRejected] = useState<boolean>(false);
  const [fileRejectedErrorMessage, setFileRejectedErrorMessage] = useState<string>('');

  const { data: withdrawalData } = useGetNonNationalSubmittedExamWithdrawal({
    uuid,
    onSuccess: (res: GetWithdrawalDraftResponse) => {
      if (res.fileEntityModelList) {
        const filesDraft: UploadedFile[] = [];
        const submittedFiles: UploadedFile[] = [];

        res.fileEntityModelList.forEach((file) => {
          const uploadedFile = {
            file: new File([new ArrayBuffer(file.fileSize)], file.fileName),
            hasError: false,
            pending: false,
            uploaded: true,
            key: file.uuid,
          };

          if (file.draft) {
            filesDraft.push(uploadedFile);
          } else {
            submittedFiles.push(uploadedFile);
          }
        });

        setDraftFiles(filesDraft);
        setFilesSubmitted(submittedFiles);
      }
    },
  });

  const usePostPresignLinksMutation = usePostPresignLinks({
    files,
    setFiles,
    setShouldSubmit,
    setApiButtonStatus,
    setErrorMsg,
  });

  const { getFilenames } = useUploadDocument({
    files,
    setFiles,
  });

  useEffect(() => {
      const allFileNames = [...files, ...draftFiles].map((f) => f.file.name);
      const hasDuplicate = new Set(allFileNames).size !== allFileNames.length;

      if (hasDuplicate) {
        setIsFileRejected(true);
        setFileRejectedErrorMessage(
          t('exam->>UploadDocument.DuplicateFileNames')
        );
      } else {
        setIsFileRejected(false);
        setFileRejectedErrorMessage('');
      }
    }, [files, draftFiles, t]);

    useEffect(() => {
      if (!isFileRejected) {
        setDisplayErrors(false);
      }
    }, [isFileRejected]);


  useEffect(() => {
    const newErrors = [];

    if (draftFiles.length < 1 && files.length < 1) {
      newErrors.push(t('withdrawal->>AdditionalDocument.NoDocumentErrMessage'));
    }
    setFormErrors(newErrors);
  }, [draftFiles, files, t]);

  const handleReview = useCallback(() => {
    history.push(`/${withdrawal}/${examRegistrations.route}/${uuid}/${nonNationalReview.route}`);
  }, [history, uuid]);

  useSetUploadButtonStatus({
    files,
    setApiButtonStatus,
    setShouldSubmit,
    shouldSubmit,
    onSuccessCallback: handleReview,
  });

  const handleBack = () =>
    history.push(`/${withdrawal}/${examRegistrations.route}/${uuid}/${nonNationalSummary.route}`);

  const handleConfirm = () => {
      if (isFileRejected) {
        setDisplayErrors(true);
        return;
      }
    if (formErrors.length < 1) {
      if (files && files.length > 0) {
        usePostPresignLinksMutation.mutate({
          payload: {
            applicationType: ApplicationType.NON_NATIONAL_EXAM,
            uuid,
            filenames: getFilenames(),
            uploadDocumentType: UploadDocumentType.SUPP_DOC,
          },
        });
      } else {
        handleReview();
      }
    } else {
      setDisplayErrors(true);
    }
  };

  useDisplayFilesExceedError(withdrawalData, setFormErrors, t);

  return (
    <>
      <WithdrawalReasonTitleHeader />
      <ContentTemplate isDividerBottom>
        <SectionHeader title={t('withdrawal->>ReviewWithdrawal.ReasonForWithdrawal')} />

        <Grid>
          <KeyValue
            id="reasonForWithdrawal"
            keyText={t('withdrawal->>AdditionalDocument.SubmittedReason')}
            valueText={withdrawalData?.reason.reasonDescription}
            capitalize={false}
          />
        </Grid>
        {withdrawalData?.specifiedReason && (
          <Grid>
            <KeyValue
              id="reasonForWithdrawal"
              keyText={t('withdrawal->>ReviewWithdrawal.SpecifiedReason')}
              valueText={withdrawalData?.specifiedReason}
              capitalize={false}
            />
          </Grid>
        )}
        <Grid>
          <KeyValue
            id="attachedFile"
            keyText={t('withdrawal->>AdditionalDocument.SubmittedDocuments')}
            valueText={
              <Grid className={classes.documentsContainer}>
                {filesSubmitted?.length === 0 ? (
                  <>-</>
                ) : (
                  <>
                    {filesSubmitted?.map((file, index) => (
                      <DocumentBadge
                        key={file.key}
                        className={index !== 0 ? classes.documentBadgeContainer : ''}
                        documentName={file.file.name}
                        fileSize={formatBytes(file.file.size)}
                      />
                    ))}
                  </>
                )}
              </Grid>
            }
            capitalize={false}
          />
        </Grid>
      </ContentTemplate>

      <ContentTemplate continuous>
        <Typography className={classes.header} variant="h3">
          {t('withdrawal->>WithdrawalReason.UploadSupportingDocuments')}
        </Typography>

        <Typography className={classes.inputLabel} variant="h5">
          {t('withdrawal->>WithdrawalReason.SupportingDocumentRequired')}
        </Typography>

         <Typography className={classes.inputLabel} variant="body1">
          {t('withdrawal->>WithdrawalReason.uploadInstruction')}
         </Typography>


        <Box className={classes.marginBottom}>
          {withdrawalData?.reason.requiredDocumentInfo.map((requiredDocument: string) => (
            <Typography key={requiredDocument}>{requiredDocument}</Typography>
          ))}
        </Box>

        {filesSubmitted && (
          <>
            <UploadDocuments
              files={files}
              setFiles={setFiles}
              hasError={displayErrors}
              submittedFiles={filesSubmitted.length}
              applicationType={ApplicationType.NON_NATIONAL_EXAM}
              draftFiles={draftFiles}
              setDraftFiles={setDraftFiles}
            />
            <ErrorInfo label={errorMsg} />
          </>
        )}

        <Box className={classes.buttonContainer}>
          <WithdrawalReasonButtons
            files={files}
            apiButtonStatus={apiButtonStatus}
            textArea={withdrawalData?.specifiedReason}
            selectReason={withdrawalData?.reason.reasonDescription}
            displayErrors={displayErrors}
            setDisplayErrors={setDisplayErrors}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            handleBack={handleBack}
            handleConfirm={handleConfirm}
            isFileRejected={isFileRejected}
            fileRejectedErrorMessage={fileRejectedErrorMessage}
            isNonNational
          />
        </Box>
      </ContentTemplate>
    </>
  );
};

export default UploadAdditionalDocument;
